import {
  COUNTRY_ECOTAX_TYPE,
  type IErrorApiResponse,
  type ISuccessApiResponse,
} from '@wyz/types';
import {
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  emptyQueryError,
  restrictApi,
  useMutation,
  useQuery,
  useQueryClient,
} from '@wyz/utils';
import { useApi } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import {
  ICart,
  ICartMeta,
  ICartValidationErrorResponse,
  IPayloadAddItemsCart,
  PurchaseErrorCodeKeys,
} from '~/purchase/types';

type Options = {
  get?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<ICart, ICartMeta>, IErrorApiResponse>
  >;
  add?: UseMutationOptions<
    ISuccessApiResponse<ICart>,
    IErrorApiResponse<unknown, PurchaseErrorCodeKeys, { stock: number }>,
    IPayloadAddItemsCart
  >;
  remove?: UseMutationOptions<
    ISuccessApiResponse<ICart>,
    IErrorApiResponse,
    Array<string>
  >;
  patch?: UseMutationOptions<
    ISuccessApiResponse<ICart>,
    IErrorApiResponse,
    { quantity: number; itemId: string }
  >;
  reset?: UseMutationOptions<ISuccessApiResponse<ICart>, IErrorApiResponse>;
  submit?: UseMutationOptions<
    ISuccessApiResponse<string[]>,
    ICartValidationErrorResponse,
    { comment?: string; submitCartContext?: 'DEFAULT' | 'CALL_CENTER' }
  >;
  refresh?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<ICart>, ICartValidationErrorResponse>
  >;
};

export const useManageCart = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions & {
    context: {
      customerId: string;
    };
  },
) => {
  const queryClient = useQueryClient();
  const { callCoreApi } = useApi();

  const idCustomer = options.context.customerId;
  const getQuery = useQuery({
    queryKey: PurchaseQueryKeys.cart.base(idCustomer),
    queryFn: () =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<ICart, ICartMeta>>(
            `/offers/api/customer/${idCustomer}/cart`,
          )
        : emptyQueryError(),
    gcTime: 0,
    refetchOnWindowFocus: false,
    placeholderData: {
      data: {
        items: [],
      },
      message: '',
      meta: {
        ecotaxType: COUNTRY_ECOTAX_TYPE.NOT_DISPLAYED,
      },
      status: 'SUCCESS',
    },
    ...options.get,
    enabled: Boolean(idCustomer) && options.get?.enabled,
  });

  const addMutation = useMutation({
    mutationFn: (variables) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<ICart>>(
            `/offers/api/customer/${idCustomer}/cart`,
            {
              body: JSON.stringify({ ...variables }),
              method: 'POST',
            },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.cart.base(idCustomer)],
    ...options.add,
  });
  const removeMutation = useMutation({
    mutationFn: (variables) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<ICart>>(
            `/offers/api/customer/${idCustomer}/cart/delete-items`,
            {
              method: 'PATCH',
              body: JSON.stringify({
                ids: variables,
              }),
            },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.cart.base(idCustomer)],
    ...options.remove,
  });

  const patchMutation = useMutation({
    mutationFn: (variables) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<ICart>>(
            `/offers/api/customer/${idCustomer}/cart/items/${variables.itemId}`,
            {
              body: JSON.stringify({
                quantity: variables.quantity,
              }),
              method: 'PATCH',
            },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.cart.base(idCustomer)],
    ...options.patch,
  });

  const resetMutation = useMutation({
    mutationFn: () =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<ICart>>(
            `/offers/api/customer/${idCustomer}/cart`,
            {
              method: 'DELETE',
            },
          )
        : emptyQueryError(),
    ...options.reset,
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.cart.base(idCustomer)],
  });

  const submitMutation = useMutation({
    mutationFn: (variables) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<string[]>>(
            `/offers/api/customer/${idCustomer}/cart/submit`,
            {
              method: 'POST',
              body: JSON.stringify(variables),
            },
          )
        : emptyQueryError(),
    onSuccess(...params) {
      queryClient.setQueryData(PurchaseQueryKeys.cart.base(idCustomer), {
        status: 'SUCCESS',
        meta: {},
        data: { items: [], comment: '' },
      } satisfies ISuccessApiResponse<ICart>);
      if (options.submit?.onSuccess) {
        options.submit.onSuccess(...params);
      }
    },
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.cart.base(idCustomer)],
    ...options.submit,
  });

  const refreshQuery = useQuery({
    queryKey: PurchaseQueryKeys.cart.refresh(idCustomer),
    queryFn: () =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<ICart>>(
            `/offers/api/customer/${idCustomer}/cart/items/refresh`,
          )
        : emptyQueryError(),
    retry: false,
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: true,
    retryOnMount: true,
    ...options.refresh,
    enabled: Boolean(options.refresh?.enabled) && getQuery.isFetched,
  });

  return restrictApi(options, {
    get: getQuery,
    add: addMutation,
    remove: removeMutation,
    patch: patchMutation,
    reset: resetMutation,
    submit: submitMutation,
    refresh: refreshQuery,
  });
};
